import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Modal,
  Container,
  useTheme,
  useMediaQuery,
  Stack,
  Fade,
  CircularProgress,
  Button,
  Tooltip,
  Chip
} from "@mui/material";
import { BrowserMultiFormatReader } from "@zxing/library";
import {
  CameraAlt,
  Close as CloseIcon,
  PhotoCamera as PhotoCameraIcon,
  QrCodeScanner as QrCodeScannerIcon,
  SwitchCamera as SwitchCameraIcon,
  Cameraswitch as CameraSwitchIcon
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  generateAccessToken,
  getSearchResultsForMealItems
} from "../../features/level3/level3Slice";
import { selectAccessToken } from "../../features/level3/level3Selectors";
import { useCurrentUser } from "./UserContext";

const QrScannerModal = ({
  open,
  setOpen,
  setFavoriteFood,
  setOpenMenu,
  setOpenSnackbar,
  setSnackbarMessage,
  setSelectedOptionData,
  setSearchQuery
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [result, setResult] = useState(null);
  const [accessToken, setAccessToken] = useState("");
  const [isScanning, setIsScanning] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cameraError, setCameraError] = useState(false);
  const [availableCameras, setAvailableCameras] = useState([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState(0);
  const [currentCameraLabel, setCurrentCameraLabel] = useState("");
  const [showCameraSwitcher, setShowCameraSwitcher] = useState(false);
  const [isAndroidDevice, setIsAndroidDevice] = useState(false);

  const existingToken = useSelector(selectAccessToken);
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const videoRef = useRef(null);
  const codeReaderRef = useRef(null);

  // Check if device is Android
  const checkAndroidDevice = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.includes("android") || userAgent.includes("linux");
  };

  const handleClose = async () => {
    if (result) {
      setIsProcessing(true);
      try {
        const { payload } = await dispatch(
          getSearchResultsForMealItems({
            barcode: result,
            token: accessToken,
            userId: currentUser?.userId
          })
        );
        if (payload?.results?.length) {
          const item = payload?.results[0];
          const data = {
            ...item,
            name: `${item.description}${
              item.brand_name ? ` (${item.brand_name})` : ""
            } `,
            id: item.serving.serving_id
          };
          setSelectedOptionData(data);
          setSearchQuery(data?.name);
        } else {
          setSnackbarMessage("No results found for the scanned barcode.");
          setOpenSnackbar(true);
        }
      } catch (error) {
        setSnackbarMessage("Error processing barcode.");
        setOpenSnackbar(true);
      } finally {
        setIsProcessing(false);
      }
    }

    // Cleanup camera
    if (codeReaderRef.current) {
      codeReaderRef.current.reset();
    }

    setOpen(false);
    setResult(null);
    setIsScanning(true);
    setCameraError(false);
    setCurrentCameraIndex(0);
    setShowCameraSwitcher(false);
  };

  const getCameraLabel = (device) => {
    const label = device.label.toLowerCase();
    if (
      label.includes("back") ||
      label.includes("rear") ||
      label.includes("environment")
    ) {
      if (label.includes("wide") || label.includes("ultra")) {
        return `Rear Wide (${device.label.split("(")[0].trim()})`;
      }
      return `Rear Camera (${device.label.split("(")[0].trim()})`;
    }
    if (
      label.includes("front") ||
      label.includes("user") ||
      label.includes("facing")
    ) {
      return `Front Camera (${device.label.split("(")[0].trim()})`;
    }
    return device.label || `Camera ${availableCameras.indexOf(device) + 1}`;
  };

  const startScanning = async (cameraIndex = 0) => {
    try {
      if (codeReaderRef.current) {
        codeReaderRef.current.reset();
      }

      const codeReader = new BrowserMultiFormatReader();
      codeReaderRef.current = codeReader;

      let videoInputDevices = await codeReader.listVideoInputDevices();

      console.log("videoInputDevices:::::::::::", videoInputDevices);
      // if (isAndroidDevice) {
      //   // Prefer back/rear camera only on Android
      videoInputDevices = videoInputDevices?.some((d) => d.deviceId)
        ? videoInputDevices
            .filter(
              (d) =>
                d.label.toLowerCase().includes("back") ||
                d.label.toLowerCase().includes("rear") ||
                d.label.toLowerCase().includes("environment")
            )
            .sort((a, b) => {
              return a.label.localeCompare(b.label);
            })
        : videoInputDevices;
      // }

      if (videoInputDevices.length === 0) {
        setCameraError(true);
        setSnackbarMessage("No camera found.");
        setOpenSnackbar(true);
        return;
      }

      setAvailableCameras(videoInputDevices);
      if (isAndroidDevice && videoInputDevices.length > 1) {
        setShowCameraSwitcher(true);
      }

      const selectedIndex = Math.min(
        cameraIndex,
        videoInputDevices?.length > 0 ? videoInputDevices.length - 1 : 0
      );
      const selectedDevice = videoInputDevices[selectedIndex];
      setCurrentCameraIndex(selectedIndex);
      setCurrentCameraLabel(getCameraLabel(selectedDevice));

      await codeReader.decodeFromVideoDevice(
        selectedDevice.deviceId,
        videoRef.current,
        (result, err) => {
          if (result) {
            setResult(result.getText());
            setIsScanning(false);
          }
          if (
            err &&
            !(err instanceof Error && err.name === "NotFoundException")
          ) {
            console.error("Decode error:", err);
          }
        }
      );

      setCameraError(false);
    } catch (error) {
      console.error("Camera access error:", error);
      setCameraError(true);
      setSnackbarMessage("Unable to access camera. Please check permissions.");
      setOpenSnackbar(true);
    }
  };

  const switchCamera = () => {
    if (availableCameras.length > 1) {
      const nextIndex = (currentCameraIndex + 1) % availableCameras.length;
      setCurrentCameraIndex(nextIndex);
      setCurrentCameraLabel(getCameraLabel(availableCameras[nextIndex]));
      startScanning(nextIndex);
    }
  };

  useEffect(() => {
    if (result) {
      handleClose();
    }
  }, [result]);

  useEffect(() => {
    setFavoriteFood("");
    setIsAndroidDevice(checkAndroidDevice());

    if (!existingToken && open) {
      dispatch(generateAccessToken()).then(({ payload }) => {
        setAccessToken(payload.access_token);
      });
    } else {
      setAccessToken(existingToken);
    }

    // Start scanning when modal opens
    if (open) {
      setIsScanning(true);
      setCameraError(false);
      setCurrentCameraIndex(0);
      setShowCameraSwitcher(false);
      setTimeout(() => {
        startScanning(0);
      }, 100);
    }

    // Cleanup on modal close
    return () => {
      if (codeReaderRef.current) {
        codeReaderRef.current.reset();
      }
    };
  }, [open]);

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const img = new Image();
        img.src = e.target.result;

        img.onload = async () => {
          const codeReader = new BrowserMultiFormatReader();
          try {
            const result = await codeReader.decodeFromImageElement(img);
            if (result?.text || result?.getText()) {
              setResult(result.text || result.getText());
              setIsScanning(false);
            } else {
              setSnackbarMessage("No barcode detected in the image.");
              setOpenSnackbar(true);
            }
          } catch (error) {
            setSnackbarMessage("Failed to decode barcode from image.");
            setOpenSnackbar(true);
          }
        };
      } catch (error) {
        console.error("Image load error", error);
        setSnackbarMessage("Unable to process uploaded image.");
        setOpenSnackbar(true);
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      disableAutoFocus
      disableEnforceFocus
      closeAfterTransition
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2
      }}
    >
      <Fade in={open}>
        <Paper
          elevation={24}
          sx={{
            width: "100%",
            maxWidth: "600px",
            maxHeight: "90vh",
            overflow: "hidden",
            borderRadius: 4,
            position: "relative",
            background: "linear-gradient(135deg, #1e1e1e, #2a2a2a)",
            color: "white",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            boxShadow: "0 10px 40px rgba(0,0,0,0.5)"
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid rgba(255, 255, 255, 0.08)"
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  borderRadius: "50%",
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: `0 0 15px ${theme.palette.primary.main}40`
                }}
              >
                <QrCodeScannerIcon sx={{ fontSize: 22 }} />
              </Box>
              <Stack spacing={0.3}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, lineHeight: 1.2 }}
                >
                  Scan Product Barcode
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.6)", lineHeight: 1.2 }}
                >
                  Position barcode within the scanner frame
                </Typography>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              {/* Camera Switcher Button for Android */}
              {showCameraSwitcher && isScanning && (
                <Tooltip title="Switch Camera">
                  <IconButton
                    onClick={switchCamera}
                    sx={{
                      color: "rgba(255,255,255,0.8)",
                      background: "rgba(255,255,255,0.1)",
                      transition: "all 0.2s",
                      "&:hover": {
                        color: "white",
                        background: "rgba(255,255,255,0.2)",
                        transform: "scale(1.05)"
                      }
                    }}
                  >
                    <SwitchCameraIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton
                onClick={handleClose}
                sx={{
                  color: "rgba(255,255,255,0.7)",
                  background: "rgba(255,255,255,0.05)",
                  transition: "all 0.2s",
                  "&:hover": {
                    color: "white",
                    background: "rgba(255,255,255,0.1)",
                    transform: "rotate(90deg)"
                  }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>

          {/* Current Camera Info for Android */}
          {isAndroidDevice && showCameraSwitcher && isScanning && (
            <Box
              sx={{
                px: 2.5,
                py: 1,
                borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
                background: "rgba(0,0,0,0.2)"
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="center"
              >
                <Chip
                  label={currentCameraLabel}
                  size="small"
                  sx={{
                    background: "rgba(255,255,255,0.1)",
                    color: "rgba(255,255,255,0.8)",
                    fontSize: "0.75rem"
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.6)" }}
                >
                  ({currentCameraIndex + 1}/{availableCameras.length})
                </Typography>
              </Stack>
            </Box>
          )}

          {/* Scanner Container */}
          <Box sx={{ position: "relative", width: "100%" }}>
            {cameraError ? (
              <Box
                sx={{
                  width: "100%",
                  height: isMobile ? "400px" : "400px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(0,0,0,0.3)",
                  gap: 2
                }}
              >
                <CameraAlt
                  sx={{ fontSize: 60, color: "rgba(255,255,255,0.5)" }}
                />
                <Typography variant="h6" sx={{ textAlign: "center" }}>
                  Camera not available
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ textAlign: "center", color: "rgba(255,255,255,0.7)" }}
                >
                  Please check camera permissions or try uploading an image
                </Typography>
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  id="barcode-upload-error"
                  onChange={handleImageUpload}
                />
                <label htmlFor="barcode-upload-error">
                  <Button
                    component="span"
                    variant="outlined"
                    startIcon={<PhotoCameraIcon />}
                    sx={{ mt: 2 }}
                  >
                    Upload Image
                  </Button>
                </label>
              </Box>
            ) : isScanning ? (
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: isMobile ? "400px" : "400px",
                  overflow: "hidden"
                }}
              >
                <video
                  ref={videoRef}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover"
                  }}
                  autoPlay
                  muted
                  playsInline
                />

                {/* Scan overlay */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: isMobile ? "280px" : "280px",
                    height: isMobile ? "200px" : "200px",
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: 2,
                    zIndex: 3,
                    boxShadow: `0 0 0 9999px rgba(0,0,0,0.5)`
                  }}
                >
                  {/* Corner indicators */}
                  <span
                    style={{
                      position: "absolute",
                      top: -2,
                      left: -2,
                      width: 20,
                      height: 20,
                      borderWidth: "2px 0 0 2px",
                      borderStyle: "solid",
                      borderColor: theme.palette.primary.main,
                      borderTopLeftRadius: 8
                    }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      top: -2,
                      right: -2,
                      width: 20,
                      height: 20,
                      borderWidth: "2px 2px 0 0",
                      borderStyle: "solid",
                      borderColor: theme.palette.primary.main,
                      borderTopRightRadius: 8
                    }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      bottom: -2,
                      left: -2,
                      width: 20,
                      height: 20,
                      borderWidth: "0 0 2px 2px",
                      borderStyle: "solid",
                      borderColor: theme.palette.primary.main,
                      borderBottomLeftRadius: 8
                    }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      bottom: -2,
                      right: -2,
                      width: 20,
                      height: 20,
                      borderWidth: "0 2px 2px 0",
                      borderStyle: "solid",
                      borderColor: theme.palette.primary.main,
                      borderBottomRightRadius: 8
                    }}
                  />

                  {/* Animated scan line */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "2px",
                      background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
                      boxShadow: `0 0 8px 2px ${theme.palette.primary.main}`,
                      animation: "scan 2s ease-in-out infinite",
                      "@keyframes scan": {
                        "0%": { top: "0%" },
                        "50%": { top: "100%" },
                        "100%": { top: "0%" }
                      }
                    }}
                  />
                </Box>

                {/* Helper text */}
                {showCameraSwitcher ? (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 20,
                      left: "50%",
                      transform: "translateX(-50%)",
                      color: "white",
                      textAlign: "center",
                      padding: "16px",
                      borderRadius: 4,
                      zIndex: 4,
                      maxWidth: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1.5,
                      background: "rgba(0,0,0,0.6)",
                      backdropFilter: "blur(10px)"
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.primary.light,
                        fontWeight: 500,
                        textAlign: "center"
                      }}
                    >
                      Not getting detected? Try switching cameras using the
                      button above
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 30,
                      left: "50%",
                      transform: "translateX(-50%)",
                      color: "white",
                      textAlign: "center",
                      padding: "16px 24px",
                      borderRadius: 4,
                      zIndex: 4,
                      maxWidth: "80%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1
                    }}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      id="barcode-upload"
                      onChange={handleImageUpload}
                    />
                    <label htmlFor="barcode-upload">
                      <Typography
                        variant="caption"
                        component="span"
                        sx={{
                          mt: 4,
                          color: theme.palette.primary.light,
                          cursor: "pointer",
                          textDecoration: "underline"
                        }}
                      >
                        Or upload an image
                      </Typography>
                    </label>
                  </Box>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: isMobile ? "350px" : "400px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(0,0,0,0.3)",
                  gap: 3
                }}
              >
                {isProcessing ? (
                  <>
                    <CircularProgress
                      size={60}
                      thickness={4}
                      sx={{ color: theme.palette.primary.main }}
                    />
                    <Typography variant="h6" sx={{ mt: 2 }}>
                      Processing barcode...
                    </Typography>
                  </>
                ) : (
                  <>
                    <Box
                      sx={{
                        backgroundColor: "rgba(0,0,0,0.5)",
                        borderRadius: 3,
                        p: 3,
                        border: `1px solid rgba(255,255,255,0.1)`,
                        maxWidth: "80%",
                        textAlign: "center"
                      }}
                    >
                      <PhotoCameraIcon
                        sx={{
                          fontSize: 50,
                          mb: 2,
                          color: theme.palette.primary.main
                        }}
                      />
                      <Typography variant="h6">Barcode detected!</Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontFamily: "monospace",
                          background: "rgba(0,0,0,0.3)",
                          p: 1,
                          mt: 1,
                          borderRadius: 1,
                          color: theme.palette.primary.main,
                          fontWeight: 600
                        }}
                      >
                        {result}
                      </Typography>
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                      Adding to your search...
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Box>

          {/* Footer with info text */}
          <Box
            sx={{
              p: 2,
              textAlign: "center",
              borderTop: "1px solid rgba(255, 255, 255, 0.08)",
              background: "rgba(0,0,0,0.2)"
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.6)" }}
            >
              {isAndroidDevice && showCameraSwitcher
                ? "Use the camera switch button if barcode is not detected properly"
                : "If using image upload option, try to only capture barcode part"}
            </Typography>
          </Box>
        </Paper>
      </Fade>
    </Modal>
  );
};

export default QrScannerModal;
